<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const cargando = ref(true);
const guardando = ref(false);

const canales = ref({
  whatsapp: { status: "disconnected", displayName: "", externalId: "", wabaId: "", accessToken: "" },
  instagram: { status: "disconnected", displayName: "", externalId: "", accessToken: "" }
});

const agenteConfig = ref({ active: false, system_prompt: "" });
const guardandoAgente = ref(false);
const knowledgeBase = ref([]);
const nuevaEntrada = ref({ question: "", answer: "" });

async function cargarAgente() {
  const [config, kb] = await Promise.all([
    api.get("/communication/ai-agent/config"),
    api.get("/communication/ai-agent/knowledge-base")
  ]);

  agenteConfig.value = {
    active: config.data.config.active,
    system_prompt: config.data.config.system_prompt || "Sos un asesor de ventas amable y directo. Respondé solo con base en la información proporcionada."
  };
  knowledgeBase.value = kb.data.entries;
}

async function guardarAgente() {
  guardandoAgente.value = true;

  try {
    await api.put("/communication/ai-agent/config", {
      active: agenteConfig.value.active,
      systemPrompt: agenteConfig.value.system_prompt
    });

    Swal.fire({ icon: "success", title: "Agente de IA actualizado", timer: 1500, showConfirmButton: false });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar la configuración",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardandoAgente.value = false;
  }
}

async function agregarEntrada() {
  if (!nuevaEntrada.value.question.trim() || !nuevaEntrada.value.answer.trim()) return;

  try {
    await api.post("/communication/ai-agent/knowledge-base", nuevaEntrada.value);
    nuevaEntrada.value = { question: "", answer: "" };
    const kb = await api.get("/communication/ai-agent/knowledge-base");
    knowledgeBase.value = kb.data.entries;
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo agregar la entrada",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

async function eliminarEntrada(entry) {
  await api.delete(`/communication/ai-agent/knowledge-base/${entry.id}`);
  knowledgeBase.value = knowledgeBase.value.filter(e => e.id !== entry.id);
}

async function cargar() {
  const res = await api.get("/communication/channels");

  const conectados = {};
  for (const c of res.data.channels) conectados[c.channel] = c;

  if (conectados.whatsapp) {
    canales.value.whatsapp = { ...canales.value.whatsapp, ...conectados.whatsapp, accessToken: "" };
  }
  if (conectados.instagram) {
    canales.value.instagram = { ...canales.value.instagram, ...conectados.instagram, accessToken: "" };
  }
}

async function conectar(channel) {
  const datos = canales.value[channel];

  if (!datos.externalId || !datos.accessToken) {
    Swal.fire({
      icon: "warning",
      title: "Faltan datos",
      text: "El ID externo y el token de acceso son obligatorios."
    });
    return;
  }

  guardando.value = true;

  try {
    await api.put(`/communication/channels/${channel}`, {
      displayName: datos.displayName,
      externalId: datos.externalId,
      accessToken: datos.accessToken,
      wabaId: datos.wabaId
    });

    await cargar();

    Swal.fire({
      icon: "success",
      title: "Canal conectado",
      timer: 1800,
      showConfirmButton: false
    });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo conectar el canal",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
}

async function desconectar(channel) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: `¿Desconectar ${channel === "whatsapp" ? "WhatsApp" : "Instagram"}?`,
    text: "Los mensajes nuevos quedarán pendientes hasta que vuelvas a conectarlo.",
    showCancelButton: true,
    confirmButtonText: "Sí, desconectar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  await api.delete(`/communication/channels/${channel}`);
  await cargar();
};

onMounted(async () => {
  cargando.value = true;
  await cargar();
  await cargarAgente();
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">
    <h4 class="mb-3 settings-header-title">
      <i class="bi bi-plug me-2"></i>
      Conexiones (WhatsApp / Instagram)
    </h4>

    <div class="alert alert-info">
      <i class="bi bi-info-circle me-1"></i>
      Necesitas una app de Meta Business verificada, con un número de WhatsApp
      (Cloud API) y/o una cuenta de Instagram profesional vinculada a una
      página de Facebook, para obtener estos datos.
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="row g-3">

      <!-- WHATSAPP -->
      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span><i class="bi bi-whatsapp text-success me-2"></i>WhatsApp</span>
            <span class="badge" :class="canales.whatsapp.status === 'connected' ? 'bg-success' : 'bg-gradient-secondary'">
              {{ canales.whatsapp.status === 'connected' ? 'Conectado' : 'Sin configurar' }}
            </span>
          </div>

          <div class="card-body">
            <div class="mb-2">
              <label>Nombre para mostrar</label>
              <argon-input v-model="canales.whatsapp.displayName" placeholder="Línea de ventas" />
            </div>

            <div class="mb-2">
              <label>Phone Number ID</label>
              <argon-input v-model="canales.whatsapp.externalId" />
            </div>

            <div class="mb-2">
              <label>WABA ID</label>
              <argon-input v-model="canales.whatsapp.wabaId" />
            </div>

            <div class="mb-2">
              <label>Access Token</label>
              <argon-input v-model="canales.whatsapp.accessToken" type="password"
                :placeholder="canales.whatsapp.status === 'connected' ? canales.whatsapp.accessToken || '••••••••' : ''" />
            </div>

            <div class="d-flex gap-2 mt-3">
              <argon-button color="success" :disabled="guardando" @click="conectar('whatsapp')">
                Guardar
              </argon-button>
              <button v-if="canales.whatsapp.status === 'connected'" class="btn btn-outline-danger" @click="desconectar('whatsapp')">
                Desconectar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- INSTAGRAM -->
      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span><i class="bi bi-instagram text-danger me-2"></i>Instagram</span>
            <span class="badge" :class="canales.instagram.status === 'connected' ? 'bg-success' : 'bg-gradient-secondary'">
              {{ canales.instagram.status === 'connected' ? 'Conectado' : 'Sin configurar' }}
            </span>
          </div>

          <div class="card-body">
            <div class="mb-2">
              <label>Nombre para mostrar</label>
              <argon-input v-model="canales.instagram.displayName" placeholder="@miagencia" />
            </div>

            <div class="mb-2">
              <label>Instagram Business Account ID</label>
              <argon-input v-model="canales.instagram.externalId" />
            </div>

            <div class="mb-2">
              <label>Access Token</label>
              <argon-input v-model="canales.instagram.accessToken" type="password"
                :placeholder="canales.instagram.status === 'connected' ? canales.instagram.accessToken || '••••••••' : ''" />
            </div>

            <div class="d-flex gap-2 mt-3">
              <argon-button color="success" :disabled="guardando" @click="conectar('instagram')">
                Guardar
              </argon-button>
              <button v-if="canales.instagram.status === 'connected'" class="btn btn-outline-danger" @click="desconectar('instagram')">
                Desconectar
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- AGENTE DE IA -->
    <div v-if="!cargando" class="card mt-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-robot text-info me-2"></i>Agente de IA</span>
        <div class="form-check form-switch mb-0">
          <input class="form-check-input" type="checkbox" role="switch" id="agenteActivo" v-model="agenteConfig.active">
          <label class="form-check-label" for="agenteActivo">{{ agenteConfig.active ? "Activo" : "Inactivo" }}</label>
        </div>
      </div>

      <div class="card-body">
        <p class="text-muted small">
          Responde automáticamente los mensajes entrantes de WhatsApp/Instagram/Messenger
          con base en la información que cargues abajo, y escala la conversación a un asesor
          cuando el cliente lo pide o la pregunta no está cubierta.
        </p>

        <div class="mb-3">
          <label>Instrucciones del agente</label>
          <textarea v-model="agenteConfig.system_prompt" class="form-control" rows="3"></textarea>
        </div>

        <argon-button color="success" :disabled="guardandoAgente" @click="guardarAgente">
          <i class="bi bi-save me-1"></i>Guardar
        </argon-button>

        <hr>

        <h6 class="mb-2">Base de conocimiento</h6>
        <ul class="list-group mb-3">
          <li v-for="entry in knowledgeBase" :key="entry.id" class="list-group-item d-flex justify-content-between align-items-start gap-2">
            <div>
              <strong>{{ entry.question }}</strong>
              <div class="text-muted small">{{ entry.answer }}</div>
            </div>
            <button class="btn btn-sm btn-outline-danger flex-shrink-0" @click="eliminarEntrada(entry)">
              <i class="bi bi-trash"></i>
            </button>
          </li>
          <li v-if="knowledgeBase.length === 0" class="list-group-item text-muted small">
            Todavía no cargaste preguntas y respuestas.
          </li>
        </ul>

        <div class="row g-2">
          <div class="col-md-5">
            <argon-input v-model="nuevaEntrada.question" placeholder="Pregunta, ej. ¿Cuánto cuesta el paquete a Bangkok?" />
          </div>
          <div class="col-md-5">
            <argon-input v-model="nuevaEntrada.answer" placeholder="Respuesta" />
          </div>
          <div class="col-md-2">
            <argon-button color="success" class="w-100" @click="agregarEntrada">Agregar</argon-button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.settings-header-title {
  color: #fff !important;
}
</style>
